import {
  Button,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
  withStyles,
  CircularProgress
} from '@material-ui/core';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@material-ui/icons';
import MuiAlert, { AlertProps, type Color } from '@material-ui/lab/Alert';
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { api } from '../../../services/api';
import { Autocomplete } from '@material-ui/lab';
import {
  cpfValidation,
  maskCEP,
  maskCPF,
  maskDateField,
  maskPhone
} from '../../../utils/masks';
import { gen, situ, tur } from '@data/colab';

import {
  Info as InfoIcon,
  Business as BusinessIcon,
  House as HouseIcon
} from '@material-ui/icons';

// Interfaces de tipo
interface ICep {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
}

interface ILocalObra {
  id_local_obra: number;
  cod_local: string;
  cnpj_local: string;
  nome: string;
  razao_social: string;
}

interface ICnpj {
  num_cnpj: string;
  razao_social: string;
}

interface IGhe {
  cod_ghe: string;
  cod_obra: string;
  id_cad_ghe: number;
  nome_ghe: string;
}

// Estilo do botão principal
const ButtonAdd = withStyles(() => ({
  root: {
    background: '#0D9C5B',
    color: '#FFF',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 500,
    padding: '8px 16px',
    textTransform: 'none',
    '&:hover': {
      background: '#FFF',
      borderColor: '#0D9C5B',
      color: '#0D9C5B',
      border: 'solid 1px #0D9C5B'
    },
    '&.Mui-disabled': {
      background: '#e0e0e0',
      color: '#a8a8a8'
    }
  }
}))(Button);

export const CadastrarColaborador = () => {
  const history = useHistory();

  // Estado do formulário principal
  const [form, setForm] = useState({
    matriculaEmpresa: '',
    matriculaEsocial: '',
    nome: '',
    cpf: '',
    rg: '',
    cnh: '',
    pis: '',
    funcao: '',
    genero: '',
    email: '',
    telefone: '',
    nascimento: '',
    dataAdmissao: '',
    hTrabalhoEntrada: '',
    hTrabalhoSaida: '',
    turno: '',
    situacao: 'Ativo',
    setor: '',
    cep: '',
    rua: '',
    numero: '',
    complemento: '',
    bairro: '',
    estado: '',
    cidade: ''
  });

  // Estado para dados da API
  const [apiData, setApiData] = useState({
    cnpjSelecionado: undefined as string | undefined,
    localObraSelecionado: undefined as string | undefined,
    gheSelecionado: undefined as string | undefined,
    razaoSocial: '',
    locaisObra: [] as ILocalObra[],
    ghes: [] as IGhe[],
    empresas: [] as ICnpj[],
    cepData: null as ICep | null
  });
  const [gheSelecionado, setGheSelecionado] = useState<IGhe | null>(null);

  // Estado da UI
  const [ui, setUi] = useState({
    loading: false,
    cpfValido: true,
    camposInvalidos: [] as string[],
    loadingLocais: false,
    loadingGHEs: false,
    loadingEmpresas: false, // Novo estado
    snackbar: {
      open: false,
      message: '',
      severity: 'success' as Color,
      cod: 0
    }
  });
  // Busca CEP com debounce e memoização
  const buscarCep = useCallback(async (cep: string) => {
    try {
      const res = await api.get(`https://viacep.com.br/ws/${cep}/json/`);
      if (res.data && !res.data.erro) {
        setApiData((prev) => ({
          ...prev,
          cepData: res.data
        }));
        setForm((prev) => ({
          ...prev,
          rua: res.data.logradouro || prev.rua,
          bairro: res.data.bairro || prev.bairro,
          estado: res.data.uf || prev.estado,
          cidade: res.data.localidade || prev.cidade
        }));
      }
    } catch (err) {
      console.error('Erro ao buscar CEP:', err);
    }
  }, []);

  // Efeito para buscar CEP
  useEffect(() => {
    const cepLimpo = form.cep.replace(/\D/g, '');
    if (cepLimpo.length === 8) {
      const timer = setTimeout(() => {
        if (
          !apiData.cepData ||
          apiData.cepData.cep.replace('-', '') !== cepLimpo
        ) {
          buscarCep(cepLimpo);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [form.cep, apiData.cepData, buscarCep]);

  // Carrega empresas ao montar o componente
  useEffect(() => {
    const carregarEmpresas = async () => {
      try {
        setUi((prev) => ({ ...prev, loadingEmpresas: true }));
        const res = await api.get('ravim/empresas-rh');
        if (res.status === 200) {
          setApiData((prev) => ({ ...prev, empresas: res.data }));
        }
      } catch (error) {
        console.error('Erro ao carregar empresas:', error);
        mostrarSnackbar('Erro ao carregar empresas', 'error');
      } finally {
        setUi((prev) => ({ ...prev, loadingEmpresas: false }));
      }
    };

    carregarEmpresas();
  }, []);

  // Carrega locais de obra quando seleciona empresa
  const carregarLocaisObra = useCallback(async (cnpj: string) => {
    try {
      setUi((prev) => ({ ...prev, loadingLocais: true }));
      const res = await api.post('ravim/empresas-rh', { num_cnpj: cnpj });
      console.log(res)
      if (res.status === 200) {
        // A API retorna local_obra como string JSON serializada (json.dumps no backend).
        // Garantir que seja sempre um array antes de usar .find() e outros metodos.
        const rawLocais = res.data.local_obra || [];
        const locais: ILocalObra[] = Array.isArray(rawLocais)
          ? rawLocais
          : typeof rawLocais === 'string'
          ? (() => { try { return JSON.parse(rawLocais); } catch { return []; } })()
          : [];
        console.log(locais)

        setApiData((prev) => ({
          ...prev,
          locaisObra: locais,
          razaoSocial: locais[0]?.razao_social || '',
          localObraSelecionado: undefined,
          ghes: [],
          gheSelecionado: undefined
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar locais de obra:', error);
      mostrarSnackbar('Erro ao carregar locais de obra', 'error');
    } finally {
      setUi((prev) => ({ ...prev, loadingLocais: false }));
    }
  }, []);

  const carregarGHEs = useCallback(async (codLocal: string) => {
    try {
      setUi((prev) => ({ ...prev, loadingGHEs: true }));
      const res = await api.post('ravim/colab-ghe', { cod_obra: codLocal });
      if (res.status === 200) {
        setApiData((prev) => ({
          ...prev,
          ghes: JSON.parse(res.data.dados) || [],
          gheSelecionado: undefined
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar GHEs:', error);
      mostrarSnackbar('Erro ao carregar GHEs', 'error');
    } finally {
      setUi((prev) => ({ ...prev, loadingGHEs: false }));
    }
  }, []);

  // Manipulador de mudança genérico
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Manipuladores específicos para campos com máscara
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valor = maskCPF(e.target.value);
    const valido = cpfValidation(valor.replace(/\D/g, ''));
    setForm((prev) => ({ ...prev, cpf: valor }));
    setUi((prev) => ({ ...prev, cpfValido: valido }));
  };

  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, telefone: maskPhone(e.target.value) }));
  };

  const handleCepChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, cep: maskCEP(e.target.value) }));
  };

  const handleDateChange = (name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: maskDateField(value) }));
  };

  // Mostrar snackbar
  const mostrarSnackbar = (message: string, severity: Color, cod = 0) => {
    setUi((prev) => ({
      ...prev,
      snackbar: {
        open: true,
        message,
        severity,
        cod
      }
    }));
  };

  // Fechar snackbar
  const fecharSnackbar = () => {
    setUi((prev) => ({
      ...prev,
      snackbar: {
        ...prev.snackbar,
        open: false
      }
    }));
  };

  // Validação do formulário
  const validarFormulario = () => {
    const camposObrigatorios = [
      { nome: 'nome', valor: form.nome },
      { nome: 'cpf', valor: form.cpf, valido: ui.cpfValido },
      { nome: 'funcao', valor: form.funcao },
      // { nome: 'dataAdmissao', valor: form.dataAdmissao },
      { nome: 'cnpj', valor: apiData.cnpjSelecionado },
      { nome: 'nascimento', valor: form.nascimento },
      { nome: 'setor', valor: form.setor }
    ];

    // Armazena quais campos estão inválidos
    const camposInvalidos = camposObrigatorios
      .filter((campo) => !campo.valor || campo.valido === false)
      .map((campo) => campo.nome);

    // Atualiza o estado para refletir os campos inválidos
    setUi((prev) => ({
      ...prev,
      camposInvalidos
    }));

    return camposInvalidos.length === 0;
  };

  // const handleApiError = (error: any) => {
  //   if (error.response) {
  //     // O servidor respondeu com um status fora do range 2xx
  //     const { data, status } = error.response;

  //     if (status === 404 || status === 409) {
  //       return data.message || 'Colaborador já cadastrado';
  //     } else if (status === 500) {
  //       return data.message || 'Erro interno no servidor';
  //     }
  //   } else if (error.request) {
  //     // A requisição foi feita mas não houve resposta
  //     return 'Sem resposta do servidor - verifique sua conexão';
  //   } else {
  //     // Algum erro ocorreu durante a configuração da requisição
  //     return 'Erro ao configurar a requisição';
  //   }

  //   return 'Erro desconhecido ao cadastrar colaborador';
  // };

  // Submissão do formulário
  const handleSubmit = async () => {
    if (!validarFormulario()) {
      mostrarSnackbar(
        'Preencha todos os campos obrigatórios corretamente',
        'error'
      );
      return;
    }

    setUi((prev) => ({ ...prev, loading: true }));

    try {
      const data = {
        nome_completo: form.nome.trim(),
        cpf: form.cpf,
        rg: form.rg.trim(),
        email: form.email.trim(),
        telefone: form.telefone.replace(/\D/g, ''),
        data_nascimento: form.nascimento.split('/').reverse().join('-'),
        genero: form.genero,
        cnh: form.cnh.trim(),
        situacao: form.situacao,
        nis: form.pis.trim(),
        funcao: form.funcao.trim(),
        setor: form.setor.trim(),
        turno: form.turno,
        data_admissao: form.dataAdmissao.split('/').reverse().join('-'),
        num_cnpj: apiData.cnpjSelecionado,
        razao_social: apiData.razaoSocial,
        horario_trab_entrada: form.hTrabalhoEntrada,
        horario_trab_saida: form.hTrabalhoSaida,
        local_obra: apiData.localObraSelecionado,
        cod_ghe: gheSelecionado?.cod_ghe,
        nome_ghe: gheSelecionado?.nome_ghe,
        rua: form.rua,
        numero: form.numero,
        cep: form.cep.replace(/\D/g, ''),
        complemento: form.complemento.trim(),
        bairro: form.bairro,
        cidade: form.cidade,
        estado: form.estado,
        matricula_empresa: form.matriculaEmpresa,
        matricula_esocial: form.matriculaEsocial
      };

      const response = await api.post('ravim/cad-colaborador', data);

      // Sucesso
      mostrarSnackbar(
        response.data.message || 'Colaborador cadastrado com sucesso',
        'success'
      );
      setTimeout(() => history.push('/colaboradores'), 2000);
    } catch (error: any) {
      // Tratamento específico para erro 409
      if (error.response && error.response.status === 409) {
        mostrarSnackbar(
          error.response.data.message || 'Colaborador já cadastrado',
          'error',
          409
        );
      }
      // Outros erros de resposta HTTP
      else if (error.response) {
        mostrarSnackbar(
          error.response.data?.message ||
            `Erro ${error.response.status}: ${error.response.statusText}`,
          'error',
          error.response.status
        );
      }
      // Erros de rede/requisição
      else if (error.request) {
        mostrarSnackbar(
          'Sem resposta do servidor - verifique sua conexão',
          'error'
        );
      }
      // Erros de configuração
      else {
        mostrarSnackbar(error.message, 'error');
      }
    } finally {
      setUi((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <Container
      maxWidth="lg"
      style={{
        padding: '24px 0',
        backgroundColor: '#f9f9f9',
        minHeight: '100vh'
      }}
    >
      <Paper
        elevation={3}
        style={{
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      >
        {/* Título e decoração */}
        <Typography
          variant="h4"
          align="center"
          style={{
            marginBottom: '32px',
            fontWeight: 600,
            color: '#2c3e50',
            position: 'relative',
            paddingBottom: '16px'
          }}
        >
          Cadastrar Colaborador
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: '#0D9C5B',
              borderRadius: '2px'
            }}
          />
        </Typography>

        {/* Seção Informações do Colaborador */}
        <div
          style={{
            marginBottom: '32px',
            padding: '16px',
            borderLeft: '4px solid #0D9C5B',
            backgroundColor: '#f0f7f4'
          }}
        >
          <Typography
            variant="h6"
            style={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <InfoIcon color="primary" />
            Informações do Colaborador
          </Typography>

          <Grid container spacing={2}>
            {/* Linha 1 */}
            <Grid item xs={12} md={3}>
              <TextField
                name="matriculaEmpresa"
                label="Matrícula Empresa"
                variant="outlined"
                value={form.matriculaEmpresa}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="matriculaEsocial"
                label="Matrícula E-social"
                variant="outlined"
                value={form.matriculaEsocial}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="nome"
                label="Nome Completo *"
                variant="outlined"
                value={form.nome}
                onChange={handleChange}
                fullWidth
                error={ui.camposInvalidos.includes('nome')}
                helperText={
                  ui.camposInvalidos.includes('nome') ? 'Campo obrigatório' : ''
                }
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            {/* Linha 2 */}
            <Grid item xs={12} md={3}>
              <TextField
                name="cpf"
                label="CPF *"
                variant="outlined"
                value={form.cpf}
                onChange={handleCpfChange}
                fullWidth
                error={!ui.cpfValido || ui.camposInvalidos.includes('cpf')}
                helperText={
                  !ui.cpfValido && form.cpf.length > 0
                    ? 'CPF inválido'
                    : ui.camposInvalidos.includes('cpf')
                    ? 'Campo obrigatório'
                    : ''
                }
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="rg"
                label="RG"
                variant="outlined"
                value={form.rg}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="cnh"
                label="CNH"
                variant="outlined"
                value={form.cnh}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="pis"
                label="PIS"
                variant="outlined"
                value={form.pis}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            {/* Linha 3 */}
            <Grid item xs={12} md={3}>
              <TextField
                name="setor"
                label="Setor *"
                variant="outlined"
                value={form.setor}
                onChange={handleChange}
                fullWidth
                error={ui.camposInvalidos.includes('setor')}
                helperText={
                  ui.camposInvalidos.includes('setor')
                    ? 'Campo obrigatório'
                    : ''
                }
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="funcao"
                label="Função *"
                variant="outlined"
                value={form.funcao}
                onChange={handleChange}
                fullWidth
                error={ui.camposInvalidos.includes('funcao')}
                helperText={
                  ui.camposInvalidos.includes('funcao')
                    ? 'Campo obrigatório'
                    : ''
                }
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Gênero</InputLabel>
                <Select
                  name="genero"
                  label="Gênero"
                  value={form.genero}
                  onChange={(e) =>
                    setForm({ ...form, genero: e.target.value as string })
                  }
                  style={{ borderRadius: '8px', backgroundColor: '#fff' }}
                >
                  {gen.map((genero) => (
                    <MenuItem value={genero.genero} key={genero.id}>
                      {genero.genero}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Linha 4 */}
            <Grid item xs={12} md={3}>
              <TextField
                name="email"
                label="E-mail"
                variant="outlined"
                value={form.email}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                name="telefone"
                label="Telefone"
                variant="outlined"
                value={form.telefone}
                onChange={handleTelefoneChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="nascimento"
                label="Data de Nascimento *"
                variant="outlined"
                value={form.nascimento}
                onChange={(e) => handleDateChange('nascimento', e.target.value)}
                fullWidth
                error={ui.camposInvalidos.includes('nascimento')}
                helperText={
                  ui.camposInvalidos.includes('nascimento')
                    ? 'Campo obrigatório'
                    : ''
                }
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="dataAdmissao"
                label="Data de Admissão"
                variant="outlined"
                value={form.dataAdmissao}
                onChange={(e) =>
                  handleDateChange('dataAdmissao', e.target.value)
                }
                fullWidth
                error={ui.camposInvalidos.includes('dataAdmissao')}
                helperText={
                  ui.camposInvalidos.includes('dataAdmissao')
                    ? 'Campo obrigatório'
                    : ''
                }
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            {/* Linha 5 */}
            <Grid item xs={12} md={3}>
              <TextField
                name="hTrabalhoEntrada"
                label="Horário de Entrada"
                type="time"
                variant="outlined"
                value={form.hTrabalhoEntrada}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="hTrabalhoSaida"
                label="Horário de Saída"
                type="time"
                variant="outlined"
                value={form.hTrabalhoSaida}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Turno</InputLabel>
                <Select
                  name="turno"
                  label="Turno"
                  value={form.turno}
                  onChange={(e) =>
                    setForm({ ...form, turno: e.target.value as string })
                  }
                  style={{ borderRadius: '8px', backgroundColor: '#fff' }}
                >
                  {tur.map((turno) => (
                    <MenuItem value={turno.turno} key={turno.id}>
                      {turno.turno}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel>Situação</InputLabel>
                <Select
                  name="situacao"
                  label="Situação"
                  value={form.situacao}
                  onChange={(e) =>
                    setForm({ ...form, situacao: e.target.value as string })
                  }
                  style={{ borderRadius: '8px', backgroundColor: '#fff' }}
                >
                  {situ.map((situacao) => (
                    <MenuItem value={situacao.situacao} key={situacao.id}>
                      {situacao.situacao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </div>

        {/* Seção Endereço */}
        <div
          style={{
            marginBottom: '32px',
            padding: '16px',
            borderLeft: '4px solid #2196F3',
            backgroundColor: '#f0f7ff'
          }}
        >
          <Typography
            variant="h6"
            style={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <HouseIcon color="primary" />
            Endereço
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                name="cep"
                label="CEP"
                variant="outlined"
                value={form.cep}
                onChange={handleCepChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                name="rua"
                label="Rua"
                variant="outlined"
                value={form.rua}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                name="numero"
                label="Número"
                variant="outlined"
                value={form.numero}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="complemento"
                label="Complemento"
                variant="outlined"
                value={form.complemento}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                name="bairro"
                label="Bairro"
                variant="outlined"
                value={form.bairro}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                name="estado"
                label="Estado"
                variant="outlined"
                value={form.estado}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                name="cidade"
                label="Cidade"
                variant="outlined"
                value={form.cidade}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#fff' }
                }}
              />
            </Grid>
          </Grid>
        </div>

        {/* Seção Empresa */}
        <div
          style={{
            marginBottom: '32px',
            padding: '16px',
            borderLeft: '4px solid #9C27B0',
            backgroundColor: '#f5f0f7'
          }}
        >
          <Typography
            variant="h6"
            style={{
              marginBottom: '16px',
              fontWeight: 600,
              color: '#2c3e50',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <BusinessIcon color="primary" />
            Empresa
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                disabled={ui.loadingEmpresas}
                options={apiData.empresas}
                getOptionLabel={(option: ICnpj) =>
                  `${option.num_cnpj} - ${option.razao_social}`
                }
                onChange={(e, newValue) => {
                  // Quando newValue é null (quando clica no X)
                  if (newValue === null) {
                    setApiData({
                      ...apiData,
                      cnpjSelecionado: undefined,
                      razaoSocial: '',
                      locaisObra: [], // Limpa a lista de locais
                      localObraSelecionado: undefined, // Limpa o local selecionado
                      ghes: [], // Limpa a lista de GHEs
                      gheSelecionado: undefined // Limpa o GHE selecionado
                    });
                    setGheSelecionado(null); // Garante que o GHE seja limpo
                  } else {
                    // Comportamento normal quando seleciona uma empresa
                    setApiData({
                      ...apiData,
                      cnpjSelecionado: newValue?.num_cnpj,
                      razaoSocial: newValue?.razao_social || ''
                    });
                    if (newValue?.num_cnpj) {
                      carregarLocaisObra(newValue.num_cnpj);
                    }
                  }
                  // Remove o erro se selecionado
                  setUi((prev) => ({
                    ...prev,
                    camposInvalidos: prev.camposInvalidos.filter(
                      (c) => c !== 'cnpj'
                    )
                  }));
                }}
                value={
                  apiData.empresas.find(
                    (e) => e.num_cnpj === apiData.cnpjSelecionado
                  ) || null
                }
                loading={ui.loadingEmpresas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="CNPJ Cadastrados *"
                    variant="outlined"
                    error={ui.camposInvalidos.includes('cnpj')}
                    helperText={
                      ui.camposInvalidos.includes('cnpj')
                        ? 'Campo obrigatório'
                        : ''
                    }
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {ui.loadingEmpresas ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      style: { borderRadius: '8px', backgroundColor: '#fff' }
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={apiData.locaisObra}
                getOptionLabel={(option: ILocalObra) =>
                  `${option.cod_local} - ${option.nome}`
                }
                onChange={(e, newValue) => {
                  setApiData({
                    ...apiData,
                    localObraSelecionado: newValue?.cod_local,
                    ghes: [],
                    gheSelecionado: undefined
                  });
                  if (newValue?.cod_local) {
                    carregarGHEs(newValue.cod_local);
                  }
                }}
                value={
                  apiData.locaisObra.find(
                    (l) => l.cod_local === apiData.localObraSelecionado
                  ) || null
                }
                loading={ui.loadingLocais}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Locais/Obras Cadastrados"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {ui.loadingLocais ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      style: { borderRadius: '8px', backgroundColor: '#fff' }
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Autocomplete
                options={apiData.ghes}
                getOptionLabel={(option: IGhe) =>
                  `${option.cod_ghe} - ${option.nome_ghe}`
                }
                onChange={(e, newValue) => {
                  setGheSelecionado(newValue);
                }}
                value={gheSelecionado}
                loading={ui.loadingGHEs}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="GHE Cadastrados"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {ui.loadingGHEs ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      style: { borderRadius: '8px', backgroundColor: '#fff' }
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Razão Social"
                variant="outlined"
                value={apiData.razaoSocial}
                disabled
                fullWidth
                InputProps={{
                  style: { borderRadius: '8px', backgroundColor: '#f5f5f5' }
                }}
              />
            </Grid>
          </Grid>
        </div>

        {/* Botões de Ação */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '16px',
            marginTop: '32px',
            paddingTop: '16px',
            borderTop: '1px solid #e0e0e0'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            style={{
              color: '#5f6c7b',
              borderColor: '#e0e0e0',
              padding: '8px 24px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500
            }}
            component={Link}
            to="/colaboradores"
          >
            Voltar
          </Button>

          <ButtonAdd
            variant="contained"
            startIcon={
              ui.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSubmit}
            disabled={ui.loading}
            style={{
              padding: '8px 24px',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none'
            }}
          >
            {ui.loading ? 'Cadastrando...' : 'Cadastrar Colaborador'}
          </ButtonAdd>
        </div>
      </Paper>

      <Snackbar
        open={ui.snackbar.open}
        autoHideDuration={6000} // Aumente o tempo para mensagens mais longas
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert
          elevation={6}
          variant="filled"
          severity={ui.snackbar.severity}
          onClose={fecharSnackbar}
          style={{ maxWidth: '500px', whiteSpace: 'pre-line' }} // Permite quebra de linha
        >
          {ui.snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Container>
  );
};
